import type { SizeData } from '@design-system/components/Pdp/PdpFitFinder.props'

/**
 * Represents a subcategory with optional sizes
 * @typedef {Object} SubCategory
 * @property {string[]} [sizes] - Array of size codes in the subcategory
 */
type SubCategory = {
  sizes?: string[]
}

/**
 * Represents a category with optional name and subcategories
 * @typedef {Object} Category
 * @property {string} [name] - Name of the category
 * @property {SubCategory[]} [subCategories] - Subcategories within the category
 */
type Category = {
  name?: string
  subCategories?: SubCategory[]
}

import countries from '../data/sizes/size-countries.json'

/**
 * Composable for retrieving size-related data
 *
 * Provides methods to fetch sizes, categories, and country information
 *
 * @returns {Promise<Object>} Size data utilities
 * @property {Object[]} countries - List of countries with size information
 * @property {Category[]} macroCategories - Detailed category structure
 * @property {Object[]} categories - Simplified category structure
 * @property {Function} getSizes - Fetch size data for given size codes
 * @property {Function} getSizesByCode - Fetch sizes for a specific size code ID
 *
 * @throws {Error} If no size configuration data is found
 *
 * @example
 * const { countries, categories, getSizes } = await useSizeRetrieveData()
 */
export const useSizeRetrieveData = async () => {
  const { currentWebsite } = useWebsite()

  /**
   * Retrieves size data for multiple size codes
   *
   * @param {string[]} items - Array of size codes to retrieve
   * @returns {Promise<Map<string, SizeData>>} Map of size codes to size data
   *
   * @example
   * const sizeMap = await getSizes(['S', 'M', 'L'])
   */
  const getSizes = async (items: string[]) => {
    const { data } = await useFetch<Record<string, SizeData>>(
      '/api/getSizesBySizeCode/getSizesByCode',
      {
        method: 'POST',
        body: { sizeCodes: items },
        key: currentWebsite.value,
        getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
      }
    )

    const sizeMap = new Map<string, SizeData>()
    const sizes = data.value
    if (sizes) {
      Object.keys(sizes).forEach(key => sizeMap.set(key, sizes[key]))
    }
    return sizeMap
  }

  /**
   * Retrieves sizes for a specific size code ID
   *
   * @param {string} sizeCodeId - Identifier for the size code
   * @returns {Promise<Object>} Fetch result with size data
   *
   * @example
   * const { data } = await getSizesByCode('mens-tops')
   */
  const getSizesByCode = async (sizeCodeId: string) => {
    return await useFetch(`/api/getSizes/${sizeCodeId}`, {
      immediate: true,
      getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
    })
  }

  // Fetch sizes configuration
  const { data } = await useSizesConfig('sizes-config')

  if (!data.value) {
    throw new Error('No data found')
  }

  // casting to category array since we know the response is deep
  const macroCategories = data.value.content
    .propertyName1 as unknown as Category[]

  /**
   * Transforms macro categories into a simplified category structure
   *
   * @type {Object[]}
   * @property {string} name - Category name
   * @property {string[]} subcategories - Flattened list of subcategory sizes
   */
  const categories =
    macroCategories?.map((category: Category) => {
      const subcategories =
        category.subCategories?.flatMap(
          subcategory => subcategory.sizes ?? []
        ) ?? []

      return {
        name: category.name ?? '',
        subcategories,
      }
    }) ?? []

  return {
    countries,
    macroCategories,
    categories,
    getSizes,
    getSizesByCode,
  }
}
